import { AuthenticatedHttpService } from 'http/httpService'
import { NewPaymentPlan, PricingPlan, PricingPlansResponse, ArticlesWithPricingPlansResponse } from './types'
import { t } from 'translations'

export class PricingPlansService {
    constructor(private httpService: AuthenticatedHttpService, private backofficeEndpoint: string) { }

    private getPricingPlansUrl(accountSlug: string): string {
        return `${this.backofficeEndpoint}api/v1/accounts/${accountSlug}/payment_plans/`
    }

    // TODO: retrieve all plans from paginated API
    // I don't think we actually need to paginate or lazy load the list, but the API is paginated, so we need to make sure we keep retrieving plans until there is no nextPage
    async getPricingPlans(accountSlug: string, articleUuid: string): Promise<PricingPlansResponse> {
        const url = `${this.getPricingPlansUrl(accountSlug)}?article_uuid=${articleUuid}&status=active&status=migrating&status=draft&status=open&status=discontinued`
        const response = await this.httpService.fetch(url, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        })

        if (!response.ok) {
            throw new Error(t('products.pricing.pricingPlans.errors.load'))
        }

        return response.json()
    }

    async createPricingPlan(accountSlug: string, paymentPlans: NewPaymentPlan[]): Promise<PricingPlansResponse> {
        const response = await this.httpService.fetch(this.getPricingPlansUrl(accountSlug), {
            method: 'POST',
            body: JSON.stringify(paymentPlans),
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        })

        if (!response.ok) {
            throw new Error(t('products.pricing.pricingPlans.errors.create'))
        }

        return response.json()
    }

    async closePricingPlan(accountSlug: string, pricingPlan: PricingPlan): Promise<void> {
        const actions = pricingPlan.paymentPlans.map((plan) => ({ action: 'close', plan_uuid: plan.planUuid }))
        const response = await this.httpService.fetch(`${this.getPricingPlansUrl(accountSlug)}actions/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(actions),
        })

        if (!response.ok) {
            throw new Error(t('products.pricing.pricingPlans.errors.close'))
        }

        return response.json()
    }

    async activatePricingPlan(accountSlug: string, pricingPlan: PricingPlan): Promise<void> {
        const actions = pricingPlan.paymentPlans.map((plan) => ({ action: 'activate', plan_uuid: plan.planUuid }))
        const response = await this.httpService.fetch(`${this.getPricingPlansUrl(accountSlug)}actions/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(actions),
        })

        if (!response.ok) {
            throw new Error(t('products.pricing.pricingPlans.errors.activate'))
        }

        return response.json()
    }

    async migratePricingPlan(accountSlug: string, pricingPlan: PricingPlan): Promise<void> {
        const actions = pricingPlan.paymentPlans.map((plan) => ({ action: 'migrate', plan_uuid: plan.planUuid }))
        const response = await this.httpService.fetch(`${this.getPricingPlansUrl(accountSlug)}actions/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(actions),
        })

        if (!response.ok) {
            throw new Error(t('products.pricing.pricingPlans.errors.migrate'))
        }

        return response.json()
    }

    async discontinuePricingPlan(accountSlug: string, pricingPlan: PricingPlan): Promise<void> {
        const actions = pricingPlan.paymentPlans.map((plan) => ({ action: 'discontinue', plan_uuid: plan.planUuid }))
        const response = await this.httpService.fetch(`${this.getPricingPlansUrl(accountSlug)}actions/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(actions),
        })

        if (!response.ok) {
            throw new Error(t('products.pricing.pricingPlans.errors.discontinue'))
        }

        return response.json()
    }

    async abortAction(accountSlug: string, pricingPlan: PricingPlan): Promise<void> {
        const actions = pricingPlan.paymentPlans.map((plan) => ({ action: 'abort', plan_uuid: plan.planUuid }))
        const response = await this.httpService.fetch(`${this.getPricingPlansUrl(accountSlug)}actions/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(actions),
        })

        if (!response.ok) {
            throw new Error(t('products.pricing.pricingPlans.errors.abort'))
        }

        return response.json()
    }

    async getArticlesWithPricingPlans(accountSlug: string): Promise<ArticlesWithPricingPlansResponse> {
        const url = `${this.getPricingPlansUrl(accountSlug)}`
        const response = await this.httpService.fetch(url, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        })

        if (!response.ok) {
            throw new Error(t('products.pricing.pricingPlans.errors.loadArticles'))
        }

        return response.json()
    }
}
